import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import {
  APP_STATUS_CACHE_KEY,
  APP_STATUS_CACHE_TTL_MS,
  APP_STATUS_PATH,
  APP_STATUS_TIMEOUT_MS,
} from '../constants';
import type { AppStatus, MaintenanceState, PlatformGate } from '../model/types';

/**
 * Читает `/app/status.json` — пороги версий, которые раздаёт nginx.
 *
 * Собственный `fetch`, а не `gqlFetcher`: тот игнорирует `options.url` (при любом непустом
 * значении адресом всё равно становится `/graphql`), да и обёртка GraphQL здесь ни к чему.
 *
 * Главное свойство — **fail-open**. Невозможность проверить версию НИКОГДА не означает
 * «версия устарела»: нет сети, таймаут, 5xx, битый JSON, 405 из-под заглушки — всё это
 * «не знаю», и приложение работает как обычно. Обратное поведение превратило бы падение
 * одного конфига в полный простой для всех, включая свежие сборки.
 */

/**
 * Отрицательные и нечисловые значения гасим в 0 = «порог выключен». Мусор в конфиге не должен
 * уметь заблокировать приложение — это последний рубеж перед экраном блокировки.
 */
const toThreshold = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const toGate = (raw: unknown): PlatformGate => {
  const source = (raw ?? {}) as Record<string, unknown>;
  const storeUrl = source.storeUrl;
  return {
    min: toThreshold(source.min),
    recommended: toThreshold(source.recommended),
    storeUrl: typeof storeUrl === 'string' && storeUrl.length > 0 ? storeUrl : null,
  };
};

/** Пустая строка из формы админки — это «нет значения», а экран не должен рисовать пустоту. */
const toText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Работы не идут. Один экземпляр на все ветки разбора: значение попадает в состояние React, и
 * общая ссылка означает, что «работ по-прежнему нет» не вызывает перерисовку.
 */
export const MAINTENANCE_OFF: MaintenanceState = { enabled: false, reason: null, until: null };

/**
 * Работы включаются ТОЛЬКО явным `true`. Строка 'true', число, чужой формат, отсутствие поля —
 * всё это «работаем»: рубильник останавливает продукт целиком, и включаться он обязан от
 * осознанного действия, а не от совпадения в разборе.
 */
const toMaintenance = (raw: unknown): MaintenanceState => {
  if (!raw || typeof raw !== 'object') {
    return MAINTENANCE_OFF;
  }
  const source = raw as Record<string, unknown>;
  if (source.enabled !== true) {
    return MAINTENANCE_OFF;
  }
  return { enabled: true, reason: toText(source.reason), until: toText(source.until) };
};

/** Приводит любой ответ к нашей форме. Отсутствующая платформа = выключенный гейт. */
export const parseAppStatus = (raw: unknown): AppStatus | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const source = raw as Record<string, unknown>;
  // Хотя бы один известный ключ должен присутствовать: иначе это не наш файл (заглушка nginx,
  // страница Cloudflare, что угодно ещё), и принимать его за конфиг нельзя.
  if (!('android' in source) && !('ios' in source)) {
    return null;
  }
  return {
    android: toGate(source.android),
    ios: toGate(source.ios),
    maintenance: toMaintenance(source.maintenance),
    // Выключается только явным `false`. Отсутствующее поле, опечатка, чужой формат — всё это
    // «разрешено»: рубильник обязан требовать осознанного действия, а не совпадения.
    reviewPrompt: source.reviewPrompt !== false,
  };
};

const statusUrl = (): string => {
  try {
    return new URL(APP_STATUS_PATH, envConfig.API_URL).toString();
  } catch {
    // API_URL относительный (локальная разработка) — тогда и статус берём относительным путём.
    return APP_STATUS_PATH;
  }
};

/**
 * Разбор сохранённой копии вместе с проверкой срока годности. Вынесено отдельной чистой
 * функцией не ради красоты: именно здесь пряталась поломка, из-за которой приложение можно
 * было запереть навсегда, и проверять её надо тестом, а не руками на устройстве.
 *
 * Что было. Копия применялась вечно, а обновить её может только удачный запрос. Значит после
 * того как порог однажды подняли, человек без сети — в метро, в самолёте, при лежащей раздаче
 * конфига — оставался с экраном блокировки, и выйти из него было нельзя: снять экран может
 * только сеть, а сеть закрыта этим же экраном. Заявлено «не знаем — пускаем», получалось
 * «не знаем — держим».
 *
 * Теперь у копии есть срок. Пока он не вышел, она работает как раньше и переживает выкат —
 * ради этого кеш и заводился. Дальше копия перестаёт существовать, решение возвращается к
 * «не знаю», и приложение открывается.
 */
export const parseCachedStatus = (raw: string | null, now: number): AppStatus | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { savedAt?: unknown; status?: unknown };
    const savedAt = typeof parsed?.savedAt === 'number' ? parsed.savedAt : null;
    // Копия без отметки времени — это формат до появления срока годности. Возраст у неё
    // неизвестен, а неизвестный возраст обязан читаться как «просрочена»: цена ошибки в другую
    // сторону — запертое приложение.
    if (savedAt === null || now - savedAt > APP_STATUS_CACHE_TTL_MS || now < savedAt) {
      return null;
    }
    const status = parseAppStatus(parsed.status);
    if (!status) {
      return null;
    }
    /*
     * Работы из копии не берём НИКОГДА — даже из свежей, даже из записанной минуту назад.
     *
     * Порог версии и рубильник работ живут в одном файле, но говорят о разном. Порог описывает
     * сборку: он верен и через сутки, ради него кеш и заведён. Рубильник описывает сервер прямо
     * сейчас, и вчерашнее «идут работы» — это уже не осторожность, а ложная остановка: работы
     * давно сняли, а человек на плохой сети получает заглушку с обещанием вернуться позавчера.
     *
     * Терять при этом нечего. Экран работ полезен ровно тогда, когда сервер отвечает и говорит
     * «я закрыт»; при недоступном сервере он не спасает, а лишь подменяет одну неработающую
     * картинку другой.
     */
    return { ...status, maintenance: MAINTENANCE_OFF };
  } catch {
    return null;
  }
};

const readCache = (): AppStatus | null =>
  parseCachedStatus(localStorage.getItem(APP_STATUS_CACHE_KEY), Date.now());

const writeCache = (status: AppStatus) => {
  try {
    localStorage.setItem(APP_STATUS_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), status }));
  } catch {
    // Хранилище закрыто — не беда, в следующий раз просто сходим в сеть.
  }
};

/**
 * @returns пороги версий либо `null`, если узнать их не удалось и в кеше ничего нет.
 *          `null` обязан трактоваться вызывающим как «ничего не показываем».
 */
export const fetchAppStatus = async (): Promise<AppStatus | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), APP_STATUS_TIMEOUT_MS);

  try {
    const res = await fetch(statusUrl(), {
      method: 'GET',
      signal: controller.signal,
      // Файл общий для всех и меняется руками — свежесть обеспечивает короткий кеш nginx,
      // а не браузер. Куки здесь не нужны и только мешали бы кешированию.
      credentials: 'omit',
    });

    if (!res.ok) {
      return readCache();
    }

    const status = parseAppStatus(await res.json());

    if (!status) {
      return readCache();
    }

    writeCache(status);
    return status;
  } catch {
    // Сеть, таймаут, HTML вместо JSON из-под заглушки — всё сюда. Последнее известное значение
    // лучше, чем ничего: если рубильник уже был поднят, он продолжит действовать.
    return readCache();
  } finally {
    clearTimeout(timer);
  }
};
